import React, { useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, CheckCircle2, FileArchive, Loader2, Upload, Video } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  createDrone360VideoUpload,
  createDroneSkyMapUpload,
  createDroneVideo,
  getDroneTypes,
  getSignedUrlForDroneUpload,
  uploadFileToGCS,
} from '@/features/upload-drone/api/drone-api';
import { handleApiError } from '@/shared/api/requests';
import { useToast } from '@/shared/hooks/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Progress } from '@/shared/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { cn } from '@/shared/utils/utils';

const uploadSchema = z
  .object({
    uploadType: z.enum(['tour', 'skyMap']),
    droneType: z.string().min(1, 'Choose the area this capture covers.'),
    date: z.string().min(1, 'Choose the capture date.'),
    file: z.custom<File>((value) => value instanceof File, 'Choose a file to upload.'),
  })
  .superRefine((values, context) => {
    if (!(values.file instanceof File)) return;
    const allowedExtensions = values.uploadType === 'tour' ? ['.zip', '.mp4'] : ['.insv'];
    if (!allowedExtensions.some((extension) => values.file.name.toLowerCase().endsWith(extension))) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['file'],
        message: `Choose a ${allowedExtensions.join(' or ')} file for this option.`,
      });
    }
  });

type DroneUploadValues = z.infer<typeof uploadSchema>;

interface DroneUploadDialogProps {
  projectId: number;
  projectName: string;
  className?: string;
}

const formatFileSize = (bytes: number) => {
  if (!bytes) return '0 bytes';
  const units = ['bytes', 'KB', 'MB', 'GB', 'TB'];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** unitIndex).toFixed(unitIndex > 1 ? 1 : 0)} ${units[unitIndex]}`;
};

export const DroneUploadDialog: React.FC<DroneUploadDialogProps> = ({
  projectId,
  projectName,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const form = useForm<DroneUploadValues>({
    resolver: zodResolver(uploadSchema),
    defaultValues: {
      uploadType: 'tour',
      droneType: '',
      date: '',
      file: undefined,
    },
  });
  const uploadType = form.watch('uploadType');
  const selectedFile = form.watch('file');

  const droneTypesQuery = useQuery({
    queryKey: ['drone-types'],
    queryFn: getDroneTypes,
    enabled: open,
    staleTime: 30 * 60 * 1000,
  });

  const uploadMutation = useMutation({
    mutationFn: async (values: DroneUploadValues) => {
      const isSkyMap = values.uploadType === 'skyMap';
      const isManual360 = values.uploadType === 'tour' && values.file.name.toLowerCase().endsWith('.mp4');
      setUploadProgress(0);
      setUploadStatus('Preparing your upload...');
      const signedUpload = await getSignedUrlForDroneUpload(
        projectId,
        values.droneType,
        values.date,
        values.file.name,
        isSkyMap ? 'sky_map_source' : isManual360 ? '360_video_source' : 'project_zip',
      );

      setUploadStatus(
        isSkyMap
          ? 'Uploading your camera file...'
          : isManual360
            ? 'Uploading your 360 video...'
            : 'Uploading your 360 tour...',
      );
      await uploadFileToGCS(
        values.file,
        signedUpload.signed_url,
        setUploadProgress,
        isSkyMap ? 'application/octet-stream' : isManual360 ? 'video/mp4' : 'application/zip',
      );

      setUploadProgress(100);
      setUploadStatus(isSkyMap || isManual360 ? 'Sending your request...' : 'Preparing your tour...');
      if (isSkyMap) {
        await createDroneSkyMapUpload({
          project: projectId,
          drone_type: values.droneType,
          date: values.date,
          uploaded_source_url: signedUpload.gcs_url,
          original_file_name: values.file.name,
        });
      } else if (isManual360) {
        await createDrone360VideoUpload({
          project: projectId,
          drone_type: values.droneType,
          date: values.date,
          uploaded_source_url: signedUpload.gcs_url,
          original_file_name: values.file.name,
        });
      } else {
        await createDroneVideo({
          project: projectId,
          drone_type: values.droneType,
          date: values.date,
          uploaded_zip_url: signedUpload.gcs_url,
        });
      }
      return { resultType: isSkyMap ? 'skyMap' : isManual360 ? 'manual360' : 'tour' } as const;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['drones', String(projectId)] });
      return {
        previousDrones: queryClient.getQueryData(['drones', String(projectId)]),
      };
    },
    onError: (error, _values, context) => {
      if (context?.previousDrones) {
        queryClient.setQueryData(['drones', String(projectId)], context.previousDrones);
      }
      form.setError('root', { message: handleApiError(error) });
      setUploadStatus('');
    },
    onSuccess: ({ resultType }) => {
      toast({
        title:
          resultType === 'skyMap'
            ? 'Sky Map request sent'
            : resultType === 'manual360'
              ? '360 video sent'
              : '360 tour uploaded',
        description:
          resultType === 'skyMap'
            ? 'Our team has been notified and will prepare the finished Sky Map.'
            : resultType === 'manual360'
              ? 'Our team has been notified and will prepare the finished 360 tour.'
              : 'Your tour is processing and will appear here when it is ready.',
      });
      form.reset();
      if (fileInputRef.current) fileInputRef.current.value = '';
      setOpen(false);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['drones', String(projectId)] });
      setUploadProgress(0);
      setUploadStatus('');
    },
  });

  const chooseUploadType = (value: 'tour' | 'skyMap') => {
    form.setValue('uploadType', value, { shouldValidate: true });
    form.resetField('file');
    form.clearErrors('file');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const acceptedExtensions = uploadType === 'tour' ? '.zip,.mp4' : '.insv';
  const acceptedFileLabel = uploadType === 'tour' ? '.zip or .mp4' : '.insv';
  const selectedMp4 = selectedFile instanceof File && selectedFile.name.toLowerCase().endsWith('.mp4');

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && uploadMutation.isPending) return;
        setOpen(nextOpen);
      }}
    >
      <DialogTrigger asChild>
        <Button className={cn('gap-2', className)} size="sm">
          <Upload className="size-4" strokeWidth={1.9} />
          Upload video
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-2xl"
        closeButtonClassName="border-border bg-muted text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl">Add a drone video</DialogTitle>
          <DialogDescription>
            Add a finished 360 tour or send us a camera file to create a 3D Sky Map for {projectName}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit((values) => uploadMutation.mutate(values))} className="space-y-5">
          <div className="space-y-2">
            <Label>What would you like to add?</Label>
            <RadioGroup
              value={uploadType}
              onValueChange={(value) => chooseUploadType(value as 'tour' | 'skyMap')}
              className="grid gap-3 sm:grid-cols-2"
              disabled={uploadMutation.isPending}
            >
              <Label
                htmlFor="drone-upload-tour"
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-[13px] border bg-muted p-4 transition-colors',
                  uploadType === 'tour' ? 'border-primary bg-primary/10 ring-1 ring-primary/30' : 'border-border hover:bg-accent',
                )}
              >
                <RadioGroupItem id="drone-upload-tour" value="tour" className="mt-1" />
                <span>
                  <span className="flex items-center gap-2 font-semibold text-foreground">
                    <Video className="size-4 text-primary" strokeWidth={1.9} /> Finished 360 tour
                  </span>
                  <span className="mt-1 block text-xs font-normal leading-relaxed text-muted-foreground">
                    Upload a ready 3DVista package (.zip), or a 360 video (.mp4) for our team to prepare.
                  </span>
                </span>
              </Label>
              <Label
                htmlFor="drone-upload-sky-map"
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-[13px] border bg-muted p-4 transition-colors',
                  uploadType === 'skyMap' ? 'border-primary bg-primary/10 ring-1 ring-primary/30' : 'border-border hover:bg-accent',
                )}
              >
                <RadioGroupItem id="drone-upload-sky-map" value="skyMap" className="mt-1" />
                <span>
                  <span className="flex items-center gap-2 font-semibold text-foreground">
                    <Box className="size-4 text-primary" strokeWidth={1.9} /> Create a 3D Sky Map
                  </span>
                  <span className="mt-1 block text-xs font-normal leading-relaxed text-muted-foreground">
                    Send the original camera file (.insv). Our team will prepare and add it for you.
                  </span>
                </span>
              </Label>
            </RadioGroup>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="drone-upload-area">Area or capture type</Label>
              <Select
                value={form.watch('droneType')}
                onValueChange={(value) => form.setValue('droneType', value, { shouldValidate: true })}
                disabled={droneTypesQuery.isLoading || uploadMutation.isPending}
              >
                <SelectTrigger id="drone-upload-area">
                  <SelectValue placeholder={droneTypesQuery.isLoading ? 'Loading areas...' : 'Choose an area'} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {(droneTypesQuery.data ?? []).map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.droneType && (
                <p className="text-xs text-destructive">{form.formState.errors.droneType.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="drone-upload-date">Capture date</Label>
              <Input
                id="drone-upload-date"
                type="date"
                disabled={uploadMutation.isPending}
                {...form.register('date')}
              />
              {form.formState.errors.date && (
                <p className="text-xs text-destructive">{form.formState.errors.date.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="drone-upload-file">Choose your {acceptedFileLabel} file</Label>
            <div className="rounded-[13px] border border-dashed border-border bg-muted p-4">
              <Input
                ref={fileInputRef}
                id="drone-upload-file"
                type="file"
                accept={acceptedExtensions}
                disabled={uploadMutation.isPending}
                className="border-0 bg-transparent p-0 file:mr-3 file:rounded-[10px] file:bg-primary/15 file:px-3 file:py-2 file:text-primary"
                onChange={(event) => {
                  form.setValue('file', event.target.files?.[0] as File, { shouldValidate: true });
                }}
              />
              {selectedFile instanceof File && (
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <FileArchive className="size-4 text-primary" strokeWidth={1.9} />
                  <span className="truncate font-medium text-foreground">{selectedFile.name}</span>
                  <span>· {formatFileSize(selectedFile.size)}</span>
                </div>
              )}
            </div>
            {form.formState.errors.file && (
              <p className="text-xs text-destructive">{form.formState.errors.file.message}</p>
            )}
          </div>

          {uploadMutation.isPending && (
            <div className="space-y-2 rounded-[13px] border border-border bg-muted p-4">
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="font-medium text-foreground">{uploadStatus}</span>
                <span className="font-display font-bold text-primary">{Math.round(uploadProgress)}%</span>
              </div>
              <Progress value={uploadProgress} />
              <p className="text-xs text-muted-foreground">Keep this window open until the upload finishes.</p>
            </div>
          )}

          {form.formState.errors.root && (
            <Alert variant="destructive">
              <AlertTitle>Upload didn’t finish</AlertTitle>
              <AlertDescription>{form.formState.errors.root.message}</AlertDescription>
            </Alert>
          )}

          {droneTypesQuery.isError && (
            <Alert variant="destructive">
              <AlertTitle>Areas couldn’t be loaded</AlertTitle>
              <AlertDescription>Close this window and try again.</AlertDescription>
            </Alert>
          )}

          {uploadType === 'skyMap' && !uploadMutation.isPending && (
            <Alert>
              <CheckCircle2 className="size-4" />
              <AlertTitle>We’ll take it from here</AlertTitle>
              <AlertDescription>
                When the upload finishes, our team will be notified to build the Sky Map and add it to this project.
              </AlertDescription>
            </Alert>
          )}

          {selectedMp4 && !uploadMutation.isPending && (
            <Alert>
              <CheckCircle2 className="size-4" />
              <AlertTitle>We’ll prepare this video</AlertTitle>
              <AlertDescription>
                When the upload finishes, our team will be notified to turn it into a viewable 360 tour and add it to this project.
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter className="gap-2 sm:space-x-0">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={uploadMutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={uploadMutation.isPending || droneTypesQuery.isError} className="gap-2">
              {uploadMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Upload className="size-4" strokeWidth={1.9} />
              )}
              {uploadMutation.isPending ? 'Uploading...' : uploadType === 'skyMap' ? 'Send for Sky Map' : 'Upload 360 tour'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
